<template>
  <div class="edit-page">
    <DefaultHeader
      title="Редактирование вакансии"
      :back="true"
      class="edit-header"
      :offset="780"
    />

    <main class="edit-content">
      <!-- Loading -->

      <div
        v-if="!isReady"
        class="page-loader"
      >
        <div class="loader-ring"></div>

        <span>
          Загружаем вакансию...
        </span>
      </div>

      <!-- Content -->

      <template v-else>
        <section class="hero-card">
          <div class="hero-decoration"></div>

          <div class="hero-icon">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M8 7V5.8A2.8 2.8 0 0 1 10.8 3h2.4A2.8 2.8 0 0 1 16 5.8V7"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
              />

              <rect
                x="3"
                y="7"
                width="18"
                height="13"
                rx="3"
                stroke="currentColor"
                stroke-width="1.8"
              />

              <path
                d="M3.5 12.5c5.5 2 11.5 2 17 0M10 13.8h4"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
              />
            </svg>
          </div>

          <div class="hero-text">
            <span class="hero-label">
              Управление вакансией
            </span>

            <h1>
              {{ vacancy.title || "Редактирование вакансии" }}
            </h1>

            <p>
              Обновите описание, условия, адрес, специализацию
              и визуальные материалы вакансии.
            </p>
          </div>

          <div
            class="changes-status"
            :class="{ active: hasChanges }"
          >
            <span class="status-dot"></span>

            {{
              hasChanges
                ? "Есть несохранённые изменения"
                : "Все изменения сохранены"
            }}
          </div>
        </section>

        <div class="editor-layout">
          <!-- Main column -->

          <div class="editor-main">
            <!-- Основная информация -->

            <section class="editor-card">
              <div class="section-header">
                <div class="section-icon">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <rect
                      x="4"
                      y="4"
                      width="16"
                      height="16"
                      rx="4"
                      stroke="currentColor"
                      stroke-width="1.8"
                    />

                    <path
                      d="M8 9h8M8 13h8M8 17h5"
                      stroke="currentColor"
                      stroke-width="1.8"
                      stroke-linecap="round"
                    />
                  </svg>
                </div>

                <div>
                  <span class="section-label">
                    Основная информация
                  </span>

                  <h2>
                    Название и описание
                  </h2>
                </div>
              </div>

              <p class="section-description">
                Используйте понятное название и подробно расскажите
                о задачах, требованиях и условиях работы.
              </p>

              <div class="form-field">
                <div class="field-heading">
                  <label for="vacancy-title">
                    Название вакансии
                  </label>

                  <span
                    class="character-counter"
                    :class="{
                      warning:
                        vacancy.title.length >=
                        TITLE_MAX_LENGTH - 10
                    }"
                  >
                    {{ vacancy.title.length }}/{{ TITLE_MAX_LENGTH }}
                  </span>
                </div>

                <div
                  class="input-shell"
                  :class="{
                    invalid:
                      titleTouched &&
                      !isTitleValid,

                    valid:
                      titleTouched &&
                      isTitleValid
                  }"
                >
                  <div class="input-icon">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      aria-hidden="true"
                    >
                      <path
                        d="M8 7V5.8A2.8 2.8 0 0 1 10.8 3h2.4A2.8 2.8 0 0 1 16 5.8V7"
                        stroke="currentColor"
                        stroke-width="1.8"
                        stroke-linecap="round"
                      />

                      <rect
                        x="3"
                        y="7"
                        width="18"
                        height="13"
                        rx="3"
                        stroke="currentColor"
                        stroke-width="1.8"
                      />
                    </svg>
                  </div>

                  <input
                    id="vacancy-title"
                    v-model="vacancy.title"
                    type="text"
                    class="text-input"
                    placeholder="Например: Senior iOS разработчик"
                    :maxlength="TITLE_MAX_LENGTH"
                    autocomplete="off"
                    @input="titleTouched = true"
                    @blur="titleTouched = true"
                  />

                  <Transition name="status">
                    <div
                      v-if="
                        titleTouched &&
                        isTitleValid
                      "
                      class="valid-status"
                    >
                      <svg
                        viewBox="0 0 20 20"
                        fill="none"
                        aria-hidden="true"
                      >
                        <path
                          d="m5.5 10.2 2.8 2.8 6.2-6.2"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </div>
                  </Transition>
                </div>

                <Transition name="message">
                  <div
                    v-if="
                      titleTouched &&
                      !isTitleValid
                    "
                    class="field-message error-message"
                  >
                    Укажите название вакансии
                  </div>
                </Transition>
              </div>

              <div class="form-field">
                <div class="field-heading">
                  <label for="vacancy-description">
                    Описание вакансии
                  </label>

                  <span
                    class="character-counter"
                    :class="{
                      warning:
                        vacancy.info.length >=
                        DESCRIPTION_MAX_LENGTH - 100
                    }"
                  >
                    {{ vacancy.info.length }}/{{ DESCRIPTION_MAX_LENGTH }}
                  </span>
                </div>

                <div
                  class="textarea-shell"
                  :class="{
                    invalid:
                      descriptionTouched &&
                      !isDescriptionValid,

                    valid:
                      descriptionTouched &&
                      isDescriptionValid
                  }"
                >
                  <textarea
                    id="vacancy-description"
                    v-model="vacancy.info"
                    class="description-textarea"
                    placeholder="Расскажите о задачах, требованиях, команде и условиях работы..."
                    :maxlength="DESCRIPTION_MAX_LENGTH"
                    @input="descriptionTouched = true"
                    @blur="descriptionTouched = true"
                  ></textarea>

                  <Transition name="status">
                    <div
                      v-if="
                        descriptionTouched &&
                        isDescriptionValid
                      "
                      class="textarea-valid-status"
                    >
                      <svg
                        viewBox="0 0 20 20"
                        fill="none"
                        aria-hidden="true"
                      >
                        <path
                          d="m5.5 10.2 2.8 2.8 6.2-6.2"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </div>
                  </Transition>
                </div>

                <Transition name="message">
                  <div
                    v-if="
                      descriptionTouched &&
                      !isDescriptionValid
                    "
                    class="field-message error-message"
                  >
                    Описание должно содержать минимум 10 символов
                  </div>
                </Transition>
              </div>
            </section>

            <!-- Условия -->

            <section class="editor-card">
              <div class="section-header">
                <div class="section-icon purple">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M5 8h14M7 12h10M9 16h6"
                      stroke="currentColor"
                      stroke-width="1.8"
                      stroke-linecap="round"
                    />

                    <rect
                      x="3"
                      y="4"
                      width="18"
                      height="16"
                      rx="4"
                      stroke="currentColor"
                      stroke-width="1.8"
                    />
                  </svg>
                </div>

                <div>
                  <span class="section-label">
                    Условия
                  </span>

                  <h2>
                    Зарплата и опыт
                  </h2>
                </div>
              </div>

              <div class="conditions-grid">
                <div class="salary-card">
                  <div class="field-heading">
                    <label for="vacancy-salary">
                      Зарплата
                    </label>

                    <span class="optional-label">
                      Необязательно
                    </span>
                  </div>

                  <div class="salary-row">
                    <div class="salary-input-shell">
                      <span class="salary-prefix">
                        от
                      </span>

                      <input
                        id="vacancy-salary"
                        v-model="vacancy.salary"
                        type="text"
                        inputmode="numeric"
                        class="salary-input"
                        placeholder="150 000"
                        @input="formatSalary"
                      />
                    </div>

                    <div class="currency-selector">
                      <button
                        v-for="currency in currencies"
                        :key="currency"
                        type="button"
                        class="currency-button"
                        :class="{
                          active:
                            vacancy.currency ===
                            currency
                        }"
                        @click="
                          vacancy.currency =
                            currency
                        "
                      >
                        {{ currency }}
                      </button>
                    </div>
                  </div>
                </div>

                <div class="experience-card">
                  <div class="experience-heading">
                    <div>
                      <span class="experience-label">
                        Необходимый опыт
                      </span>

                      <span class="experience-description">
                        Требуемый стаж кандидата
                      </span>
                    </div>

                    <div class="experience-value">
                      {{ experienceText }}
                    </div>
                  </div>

                  <input
                    v-model.number="vacancy.experience"
                    type="range"
                    min="0"
                    max="10"
                    step="1"
                    class="experience-slider"
                    :style="experienceSliderStyle"
                  />

                  <div class="slider-labels">
                    <span>
                      Без опыта
                    </span>

                    <span>
                      10+ лет
                    </span>
                  </div>
                </div>
              </div>

              <div class="specific-section">
                <div class="field-heading">
                  <label>
                    Специализация
                  </label>
                </div>

                <div class="specific-wrapper">
                  <SpecificDropdown
                    v-model="vacancy.specific"
                  />
                </div>
              </div>
            </section>

            <!-- Работа и адрес -->

            <section class="editor-card">
              <div class="section-header">
                <div class="section-icon green">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M12 21s7-5.25 7-12a7 7 0 1 0-14 0c0 6.75 7 12 7 12Z"
                      stroke="currentColor"
                      stroke-width="1.8"
                      stroke-linejoin="round"
                    />

                    <circle
                      cx="12"
                      cy="9"
                      r="2.5"
                      stroke="currentColor"
                      stroke-width="1.8"
                    />
                  </svg>
                </div>

                <div>
                  <span class="section-label">
                    Формат работы
                  </span>

                  <h2>
                    Офис или удалёнка
                  </h2>
                </div>
              </div>

              <label
                class="remote-card"
                :class="{
                  active: vacancy.isRemote
                }"
              >
                <div class="remote-icon">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <rect
                      x="3.5"
                      y="5"
                      width="17"
                      height="11"
                      rx="2"
                      stroke="currentColor"
                      stroke-width="1.7"
                    />

                    <path
                      d="M8 20h8M12 16v4"
                      stroke="currentColor"
                      stroke-width="1.7"
                      stroke-linecap="round"
                    />
                  </svg>
                </div>

                <div class="remote-content">
                  <strong>
                    Удалённая работа
                  </strong>

                  <span>
                    Сотрудник может работать из любой точки
                  </span>
                </div>

                <input
                  v-model="vacancy.isRemote"
                  type="checkbox"
                  class="toggle-input"
                  @change="handleRemoteChange"
                />

                <span class="toggle-slider">
                  <span class="toggle-thumb"></span>
                </span>
              </label>

              <Transition
                name="address"
                mode="out-in"
              >
                <div
                  v-if="!vacancy.isRemote"
                  key="office"
                  class="address-content"
                >
                  <div class="field-heading">
                    <label for="vacancy-address">
                      Адрес офиса
                    </label>
                  </div>

                  <div class="input-shell address-shell">
                    <div class="input-icon">
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        aria-hidden="true"
                      >
                        <path
                          d="M12 21s7-5.25 7-12a7 7 0 1 0-14 0c0 6.75 7 12 7 12Z"
                          stroke="currentColor"
                          stroke-width="1.8"
                          stroke-linejoin="round"
                        />

                        <circle
                          cx="12"
                          cy="9"
                          r="2.5"
                          stroke="currentColor"
                          stroke-width="1.8"
                        />
                      </svg>
                    </div>

                    <input
                      id="vacancy-address"
                      v-model="address"
                      type="text"
                      class="text-input"
                      placeholder="Москва, ул. Тверская, 12"
                      autocomplete="street-address"
                    />
                  </div>

                  <div class="map-card">
                    <iframe
                      v-if="mapsApiKey"
                      :key="mapUrl"
                      class="map-iframe"
                      :src="mapUrl"
                      title="Адрес вакансии"
                      loading="lazy"
                      allowfullscreen
                      referrerpolicy="no-referrer-when-downgrade"
                    ></iframe>

                    <div
                      v-else
                      class="map-placeholder"
                    >
                      <div class="map-placeholder-icon">
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          aria-hidden="true"
                        >
                          <path
                            d="M12 21s7-5.25 7-12a7 7 0 1 0-14 0c0 6.75 7 12 7 12Z"
                            stroke="currentColor"
                            stroke-width="1.8"
                            stroke-linejoin="round"
                          />

                          <circle
                            cx="12"
                            cy="9"
                            r="2.5"
                            stroke="currentColor"
                            stroke-width="1.8"
                          />
                        </svg>
                      </div>

                      <strong>
                        Карта недоступна
                      </strong>

                    </div>

                    <div class="map-bottom-gradient"></div>

                    <div class="map-address">
                      <div class="map-address-icon">
                        <svg
                          viewBox="0 0 20 20"
                          fill="none"
                          aria-hidden="true"
                        >
                          <path
                            d="M10 18s5.5-4.2 5.5-9.5a5.5 5.5 0 1 0-11 0C4.5 13.8 10 18 10 18Z"
                            stroke="currentColor"
                            stroke-width="1.5"
                          />

                          <circle
                            cx="10"
                            cy="8.5"
                            r="1.8"
                            stroke="currentColor"
                            stroke-width="1.5"
                          />
                        </svg>
                      </div>

                      <div>
                        <span>
                          Адрес вакансии
                        </span>

                        <strong>
                          {{
                            address.trim() ||
                            "Адрес пока не указан"
                          }}
                        </strong>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  v-else
                  key="remote"
                  class="remote-state"
                >
                  <div class="remote-state-icon">
                    <svg
                      viewBox="0 0 32 32"
                      fill="none"
                      aria-hidden="true"
                    >
                      <rect
                        x="4"
                        y="6"
                        width="24"
                        height="16"
                        rx="3"
                        stroke="currentColor"
                        stroke-width="2"
                      />

                      <path
                        d="M11 27h10M16 22v5"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                      />

                      <path
                        d="m12 14 2.5 2.5L20.5 11"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </div>

                  <div>
                    <strong>
                      Работа из любой точки
                    </strong>

                    <span>
                      Адрес офиса не будет отображаться
                      в карточке вакансии.
                    </span>
                  </div>
                </div>
              </Transition>
            </section>

            <!-- Хэштеги -->

            <section class="editor-card">
              <div class="section-header">
                <div class="section-icon orange">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M9 3 7 21M17 3l-2 18M4 9h16M3 15h16"
                      stroke="currentColor"
                      stroke-width="1.8"
                      stroke-linecap="round"
                    />
                  </svg>
                </div>

                <div>
                  <span class="section-label">
                    Категории
                  </span>

                  <h2>
                    Хэштеги
                  </h2>
                </div>
              </div>

              <p class="section-description">
                Хэштеги помогают кандидатам быстрее находить
                подходящие вакансии.
              </p>

              <div
                class="hashtags-editor"
                role="button"
                tabindex="0"
                @click="openHashtagDrawer"
                @keydown.enter="openHashtagDrawer"
              >
                <div
                  v-if="vacancy.hashtags.length"
                  class="hashtags-list"
                >
                  <span
                    v-for="hashtag in vacancy.hashtags"
                    :key="
                      hashtag.id ||
                      hashtag._id ||
                      hashtag.name
                    "
                    class="hashtag-chip"
                  >
                    #{{ hashtag.name || hashtag }}
                  </span>
                </div>

                <div
                  v-else
                  class="hashtags-empty"
                >
                  Хэштеги пока не выбраны
                </div>

                <div class="hashtags-action">
                  <span>
                    Изменить
                  </span>

                  <svg
                    viewBox="0 0 20 20"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="m7 4 6 6-6 6"
                      stroke="currentColor"
                      stroke-width="1.8"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </div>
              </div>
            </section>

            <!-- Изображение -->

            <section class="editor-card">
              <div class="section-header">
                <div class="section-icon pink">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <rect
                      x="3"
                      y="5"
                      width="18"
                      height="14"
                      rx="3"
                      stroke="currentColor"
                      stroke-width="1.8"
                    />

                    <circle
                      cx="9"
                      cy="10"
                      r="2"
                      stroke="currentColor"
                      stroke-width="1.6"
                    />

                    <path
                      d="m5.5 17 4.2-4 3.2 2.8 2.5-2.2L19 17"
                      stroke="currentColor"
                      stroke-width="1.7"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </div>

                <div>
                  <span class="section-label">
                    Обложка
                  </span>

                  <h2>
                    Изображение вакансии
                  </h2>
                </div>
              </div>

              <p class="section-description">
                Добавьте изображение, которое будет отображаться
                в ленте и карточке вакансии.
              </p>

              <div
                class="upload-area"
                :class="{
                  loading: imageLoading,
                  dragging: isDragging
                }"
                role="button"
                tabindex="0"
                @click="triggerFileInput"
                @keydown.enter="triggerFileInput"
                @dragenter.prevent="isDragging = true"
                @dragover.prevent="isDragging = true"
                @dragleave.prevent="isDragging = false"
                @drop.prevent="handleDrop"
              >
                <input
                  ref="fileInput"
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  class="hidden-input"
                  @change="handleFileChange"
                />

                <Transition
                  name="image"
                  mode="out-in"
                >
                  <div
                    v-if="imageLoading"
                    key="loading"
                    class="upload-loading"
                  >
                    <div class="loader-ring"></div>

                    <strong>
                      Загружаем изображение
                    </strong>

                    <span>
                      Пожалуйста, не закрывайте страницу
                    </span>
                  </div>

                  <div
                    v-else-if="imageUrl"
                    key="preview"
                    class="image-preview"
                  >
                    <img
                      :src="imageUrl"
                      alt="Изображение вакансии"
                    />

                    <div class="image-overlay">
                      <div class="image-overlay-content">
                        <div class="replace-image-icon">
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            aria-hidden="true"
                          >
                            <path
                              d="M4 16.5V19a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-2.5"
                              stroke="currentColor"
                              stroke-width="1.8"
                              stroke-linecap="round"
                            />

                            <path
                              d="m8 10 4-4 4 4M12 6v9"
                              stroke="currentColor"
                              stroke-width="1.8"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                          </svg>
                        </div>

                        <strong>
                          Заменить изображение
                        </strong>
                      </div>
                    </div>

                    <button
                      type="button"
                      class="remove-image-button"
                      @click.stop="removeImage"
                    >
                      <svg
                        viewBox="0 0 20 20"
                        fill="none"
                        aria-hidden="true"
                      >
                        <path
                          d="M6.5 6.5 13.5 13.5M13.5 6.5 6.5 13.5"
                          stroke="currentColor"
                          stroke-width="1.8"
                          stroke-linecap="round"
                        />
                      </svg>

                      Удалить
                    </button>
                  </div>

                  <div
                    v-else
                    key="placeholder"
                    class="upload-placeholder"
                  >
                    <div class="upload-placeholder-icon">
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        aria-hidden="true"
                      >
                        <path
                          d="m8 11 4-4 4 4M12 7v10"
                          stroke="currentColor"
                          stroke-width="1.8"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />

                        <path
                          d="M4 16.5V19a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-2.5"
                          stroke="currentColor"
                          stroke-width="1.8"
                          stroke-linecap="round"
                        />
                      </svg>
                    </div>

                    <strong>
                      Добавьте изображение
                    </strong>

                    <span>
                      Нажмите или перетащите JPG, PNG или WEBP
                    </span>

                    <small>
                      Максимальный размер — 10 МБ
                    </small>
                  </div>
                </Transition>
              </div>

              <Transition name="message">
                <div
                  v-if="imageError"
                  class="upload-error"
                >
                  {{ imageError }}
                </div>
              </Transition>
            </section>
          </div>

          <!-- Sidebar -->

          <aside class="editor-sidebar">
            <div class="sidebar-sticky">
              <section class="preview-card">
                <span class="preview-label">
                  Предпросмотр
                </span>

                <div class="vacancy-preview">
                  <div class="preview-image-wrapper">
                    <img
                      v-if="imageUrl"
                      :src="imageUrl"
                      alt=""
                    />

                    <div
                      v-else
                      class="preview-placeholder"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        aria-hidden="true"
                      >
                        <rect
                          x="3"
                          y="5"
                          width="18"
                          height="14"
                          rx="3"
                          stroke="currentColor"
                          stroke-width="1.8"
                        />

                        <path
                          d="m5.5 17 4.2-4 3.2 2.8 2.5-2.2L19 17"
                          stroke="currentColor"
                          stroke-width="1.7"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </div>

                    <span class="preview-work-type">
                      {{
                        vacancy.isRemote
                          ? "Удалёнка"
                          : "Офис"
                      }}
                    </span>
                  </div>

                  <div class="preview-content">
                    <span class="preview-specific">
                      {{
                        vacancy.specific?.name ||
                        "Специализация"
                      }}
                    </span>

                    <h3>
                      {{
                        vacancy.title ||
                        "Название вакансии"
                      }}
                    </h3>

                    <p>
                      {{
                        vacancy.info ||
                        "Здесь появится описание вашей вакансии."
                      }}
                    </p>

                    <div class="preview-meta">
                      <span>
                        {{ formattedSalary }}
                      </span>

                      <span>
                        {{ experienceText }}
                      </span>
                    </div>
                  </div>
                </div>
              </section>

              <section class="save-card">
                <div class="save-card-heading">
                  <strong>
                    Сохранение
                  </strong>

                  <span>
                    Проверьте заполненные данные перед публикацией.
                  </span>
                </div>

                <div class="validation-list">
                  <div
                    class="validation-item"
                    :class="{ valid: isTitleValid }"
                  >
                    <span class="validation-icon">
                      ✓
                    </span>

                    Название вакансии
                  </div>

                  <div
                    class="validation-item"
                    :class="{
                      valid: isDescriptionValid
                    }"
                  >
                    <span class="validation-icon">
                      ✓
                    </span>

                    Описание вакансии
                  </div>

                  <div
                    class="validation-item"
                    :class="{
                      valid:
                        vacancy.isRemote ||
                        address.trim().length > 0
                    }"
                  >
                    <span class="validation-icon">
                      ✓
                    </span>

                    Формат работы
                  </div>
                </div>

                <button
                  type="button"
                  class="save-button"
                  :disabled="
                    !canSave ||
                    isSaving ||
                    imageLoading
                  "
                  @click="saveVacancy"
                >
                  <span
                    v-if="isSaving"
                    class="button-loader"
                  ></span>

                  <svg
                    v-else
                    viewBox="0 0 20 20"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="m5.5 10.2 2.8 2.8 6.2-6.2"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>

                  {{
                    isSaving
                      ? "Сохраняем..."
                      : "Сохранить изменения"
                  }}
                </button>

                <button
                  type="button"
                  class="cancel-button"
                  :disabled="isSaving"
                  @click="handleCancel"
                >
                  Отмена
                </button>
              </section>
            </div>
          </aside>
        </div>
      </template>
    </main>

    <Footer class="edit-footer" />

    <HashtagDrawer
      v-if="isHashtagDrawerOpen"
      :model-value="vacancy.hashtags"
      @update:modelValue="updateHashtags"
      @close="isHashtagDrawerOpen = false"
    />

    <Snackbar
      :message="snackbarMessage"
      :type="snackbarType"
      :show="snackbarShow"
    />
  </div>
</template>

<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch
} from "vue";

import {
  onBeforeRouteLeave,
  useRoute,
  useRouter
} from "vue-router";

import DefaultHeader
  from "../components/DefaultHeader.vue";

import SpecificDropdown
  from "../components/SpecificDropdown.vue";

import Footer
  from "../components/MiniFooter.vue";

import HashtagDrawer
  from "../views/HashtagDrawer.vue";

import Snackbar
  from "../components/Snackbar.vue";

import MyVacanciesService
  from "../api/MyVacanciesService";

const TITLE_MAX_LENGTH = 80;
const DESCRIPTION_MAX_LENGTH = 5000;
const MAX_IMAGE_SIZE = 10 * 1024 * 1024;

const ALLOWED_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp"
];

const router = useRouter();
const route = useRoute();

const currencies = [
  "₽",
  "$",
  "€"
];

const mapsApiKey = "AIzaSyBSEN-wo__FMd5MeQUIkQYGFPuqZeHgVNY";
const vacancy = reactive({
  id: "",
  title: "",
  info: "",
  salary: "",
  currency: "₽",
  experience: 0,
  specific: null,
  hashtags: [],
  isRemote: false,
  imageUrl: ""
});

const originalState = ref("");

const isReady = ref(false);
const isSaving = ref(false);
const isHashtagDrawerOpen = ref(false);

const titleTouched = ref(false);
const descriptionTouched = ref(false);

const address = ref("");

const imageUrl = ref("");
const uploadedImageUrl = ref("");
const selectedImageFile = ref(null);
const imageLoading = ref(false);
const imageError = ref("");
const isDragging = ref(false);
const fileInput = ref(null);

const previewObjectUrl = ref(null);

const snackbarMessage = ref("");
const snackbarType = ref("success");
const snackbarShow = ref(false);

const isTitleValid = computed(() => {
  return vacancy.title.trim().length >= 2;
});

const isDescriptionValid = computed(() => {
  return vacancy.info.trim().length >= 10;
});

const isFormValid = computed(() => {
  return (
    isTitleValid.value &&
    isDescriptionValid.value
  );
});

const currentState = computed(() => {
  return JSON.stringify({
    id: vacancy.id,
    title: vacancy.title.trim(),
    info: vacancy.info.trim(),
    salary: vacancy.salary,
    currency: vacancy.currency,
    experience: Number(vacancy.experience),
    specific: vacancy.specific,
    hashtags: vacancy.hashtags,
    isRemote: vacancy.isRemote,
    address: address.value.trim(),
    imageUrl:
      uploadedImageUrl.value ||
      imageUrl.value ||
      ""
  });
});

const hasChanges = computed(() => {
  if (!isReady.value) {
    return false;
  }

  return currentState.value !== originalState.value;
});

const canSave = computed(() => {
  return (
    hasChanges.value &&
    isFormValid.value
  );
});

const experienceText = computed(() => {
  const experience =
    Number(vacancy.experience) || 0;

  if (experience === 0) {
    return "Без опыта";
  }

  if (experience === 1) {
    return "1 год";
  }

  if (
    experience >= 2 &&
    experience <= 4
  ) {
    return `${experience} года`;
  }

  if (experience >= 10) {
    return "10+ лет";
  }

  return `${experience} лет`;
});

const experienceSliderStyle = computed(() => {
  const progress =
    (Number(vacancy.experience) / 10) * 100;

  return {
    "--slider-progress": `${progress}%`
  };
});

const formattedSalary = computed(() => {
  const salary =
    String(vacancy.salary || "")
      .replace(/\D/g, "");

  if (!salary) {
    return "Зарплата не указана";
  }

  return (
    `от ${Number(salary).toLocaleString("ru-RU")} ` +
    vacancy.currency
  );
});

const mapQuery = computed(() => {
  return address.value.trim() || "Москва";
});

const mapUrl = computed(() => {
  if (!mapsApiKey) {
    return "";
  }

  return (
    "https://www.google.com/maps/embed/v1/place" +
    `?key=${mapsApiKey}` +
    `&q=${encodeURIComponent(mapQuery.value)}` +
    "&zoom=15"
  );
});

onMounted(() => {
  loadVacancy();

  window.addEventListener(
    "beforeunload",
    handleBeforeUnload
  );
});

onBeforeUnmount(() => {
  window.removeEventListener(
    "beforeunload",
    handleBeforeUnload
  );

  revokePreviewObjectUrl();
});

onBeforeRouteLeave(() => {
  if (
    hasChanges.value &&
    !isSaving.value
  ) {
    return window.confirm(
      "Есть несохранённые изменения. Покинуть страницу?"
    );
  }

  return true;
});

function loadVacancy() {
  try {
    const storedVacancy =
      localStorage.getItem(
        "vacancy_to_edit"
      );

    if (!storedVacancy) {
      showSnackbar(
        "Не удалось найти данные вакансии",
        "error"
      );

      isReady.value = true;
      return;
    }

    const parsed =
      JSON.parse(storedVacancy);

    vacancy.id =
      parsed.id ||
      parsed._id ||
      route.params.id ||
      "";

    vacancy.title =
      parsed.title || "";

    vacancy.info =
      parsed.info || "";

    vacancy.salary =
      String(parsed.salary || "");

    vacancy.currency =
      parsed.currency ||
      parsed.salary_currency ||
      "₽";

    vacancy.experience =
      Number(parsed.experience || 0);

    vacancy.specific =
      parsed.specific || null;

    vacancy.hashtags =
      Array.isArray(parsed.hashtags)
        ? parsed.hashtags
        : [];

    vacancy.isRemote =
      Boolean(
        parsed.isRemote ??
        parsed.is_remote
      );

    address.value =
      parsed.street ||
      parsed.address ||
      localStorage.getItem(
        "vacancy_address"
      ) ||
      "";

    imageUrl.value =
      parsed.image_url ||
      parsed.imageUrl ||
      "";

    uploadedImageUrl.value =
      imageUrl.value;

    nextTick(() => {
      originalState.value =
        currentState.value;

      isReady.value = true;
    });
  } catch (error) {
    console.error(
      "❌ Ошибка загрузки вакансии:",
      error
    );

    showSnackbar(
      "Не удалось загрузить данные вакансии",
      "error"
    );

    isReady.value = true;
  }
}

function handleBeforeUnload(event) {
  if (!hasChanges.value) {
    return;
  }

  event.preventDefault();
  event.returnValue = "";
}

function formatSalary(event) {
  const digits =
    event.target.value
      .replace(/\D/g, "")
      .slice(0, 10);

  vacancy.salary =
    digits
      ? Number(digits).toLocaleString(
          "ru-RU"
        )
      : "";
}

function handleRemoteChange() {
  if (vacancy.isRemote) {
    address.value = "";
  }
}

function openHashtagDrawer() {
  isHashtagDrawerOpen.value = true;
}

function updateHashtags(value) {
  vacancy.hashtags =
    Array.isArray(value)
      ? value
      : [];
}

function triggerFileInput() {
  if (imageLoading.value) {
    return;
  }

  fileInput.value?.click();
}

function handleFileChange(event) {
  const file =
    event.target.files?.[0];

  event.target.value = "";

  if (file) {
    uploadImage(file);
  }
}

function handleDrop(event) {
  isDragging.value = false;

  const file =
    event.dataTransfer.files?.[0];

  if (file) {
    uploadImage(file);
  }
}

async function uploadImage(file) {
  const validationError =
    validateImage(file);

  if (validationError) {
    imageError.value =
      validationError;

    return;
  }

  imageError.value = "";
  imageLoading.value = true;
  selectedImageFile.value = file;

  revokePreviewObjectUrl();

  previewObjectUrl.value =
    URL.createObjectURL(file);

  imageUrl.value =
    previewObjectUrl.value;

  try {
    const uploadedUrl =
      await MyVacanciesService
        .uploadVacancyImage(
          vacancy.id,
          file
        );

    if (!uploadedUrl) {
      throw new Error(
        "Сервис не вернул ссылку на изображение"
      );
    }

    uploadedImageUrl.value =
      uploadedUrl;

    imageUrl.value =
      uploadedUrl;

    selectedImageFile.value = null;

    revokePreviewObjectUrl();

    showSnackbar(
      "Изображение загружено"
    );
  } catch (error) {
    console.error(
      "❌ Ошибка загрузки изображения:",
      error
    );

    imageError.value =
      "Не удалось загрузить изображение";

    imageUrl.value =
      uploadedImageUrl.value || "";

    selectedImageFile.value = null;

    revokePreviewObjectUrl();
  } finally {
    imageLoading.value = false;
  }
}

function validateImage(file) {
  if (
    !ALLOWED_IMAGE_TYPES.includes(
      file.type
    )
  ) {
    return "Поддерживаются только JPG, PNG и WEBP";
  }

  if (
    file.size >
    MAX_IMAGE_SIZE
  ) {
    return "Размер изображения не должен превышать 10 МБ";
  }

  return "";
}

function removeImage() {
  revokePreviewObjectUrl();

  imageUrl.value = "";
  uploadedImageUrl.value = "";
  selectedImageFile.value = null;
  imageError.value = "";

  if (fileInput.value) {
    fileInput.value.value = "";
  }
}

function revokePreviewObjectUrl() {
  if (!previewObjectUrl.value) {
    return;
  }

  URL.revokeObjectURL(
    previewObjectUrl.value
  );

  previewObjectUrl.value = null;
}

async function saveVacancy() {
  if (
    isSaving.value ||
    !canSave.value
  ) {
    titleTouched.value = true;
    descriptionTouched.value = true;

    return;
  }

  isSaving.value = true;

  try {
    const payload = {
      vacancy_id: vacancy.id,

      title:
        vacancy.title.trim(),

      info:
        vacancy.info.trim(),

      salary:
        String(vacancy.salary)
          .replace(/\D/g, ""),

      currency:
        vacancy.currency,

      specific:
        vacancy.specific
          ? {
              id:
                vacancy.specific.id ||
                vacancy.specific._id,

              name:
                vacancy.specific.name,

              eng_name:
                vacancy.specific.eng_name
            }
          : null,

      is_remote:
        vacancy.isRemote,

      street:
        vacancy.isRemote
          ? ""
          : address.value.trim(),

      experience:
        Number(
          vacancy.experience
        ),

      hashtags:
        vacancy.hashtags || [],

      image_url:
        uploadedImageUrl.value || ""
    };

    console.log(
      "💾 Сохраняем вакансию:",
      payload
    );

    await MyVacanciesService
      .editMyVacancy(payload);

    originalState.value =
      currentState.value;

    localStorage.setItem(
      "vacancy_to_edit",
      JSON.stringify({
        ...vacancy,
        image_url:
          uploadedImageUrl.value,
        street:
          address.value
      })
    );

    showSnackbar(
      "Вакансия успешно сохранена"
    );

    window.setTimeout(() => {
      router.push(
        "/my-vacancies"
      );
    }, 900);
  } catch (error) {
    console.error(
      "❌ Ошибка сохранения вакансии:",
      error
    );

    showSnackbar(
      error.response?.data?.error ||
      error.message ||
      "Не удалось сохранить вакансию",
      "error"
    );
  } finally {
    isSaving.value = false;
  }
}

function handleCancel() {
  if (
    hasChanges.value &&
    !window.confirm(
      "Отменить изменения?"
    )
  ) {
    return;
  }

  router.back();
}

function showSnackbar(
  message,
  type = "success"
) {
  snackbarMessage.value = message;
  snackbarType.value = type;
  snackbarShow.value = false;

  nextTick(() => {
    snackbarShow.value = true;
  });
}
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.edit-page {
  --page-background: #f3f5f8;
  --card-background: #ffffff;
  --card-border: rgba(30, 50, 80, 0.07);
  --text-primary: #20242c;
  --text-secondary: #737b88;
  --text-muted: #949ba7;
  --input-background: #f6f7f9;
  --input-border: #e0e4e9;
  --input-hover: #fafbfc;

  width: 100%;
  min-height: 100dvh;

  display: flex;
  flex-direction: column;

  color: var(--text-primary);
  background:
    radial-gradient(
      circle at 15% 0%,
      rgba(40, 135, 247, 0.08),
      transparent 420px
    ),
    radial-gradient(
      circle at 95% 15%,
      rgba(110, 84, 232, 0.06),
      transparent 440px
    ),
    var(--page-background);
}

.edit-header {
  flex-shrink: 0;
}

.edit-content {
  width: 100%;
  max-width: 1440px;
  flex: 1;

  margin: 0 auto;
  padding: 32px;
}

.page-loader {
  min-height: 500px;

  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;

  gap: 16px;

  color: var(--text-secondary);

  font-family: "Eastman-Regular", sans-serif;
  font-size: 13px;
}

/* Hero */

.hero-card {
  position: relative;

  width: 100%;

  display: flex;
  align-items: center;
  gap: 18px;

  overflow: hidden;

  margin-bottom: 24px;
  padding: 24px 26px;

  background:
    linear-gradient(
      135deg,
      rgba(40, 135, 247, 0.1),
      rgba(99, 83, 232, 0.06)
    ),
    var(--card-background);

  border: 1px solid rgba(58, 127, 235, 0.12);
  border-radius: 24px;

  box-shadow:
    0 12px 34px
    rgba(24, 47, 83, 0.045);
}

.hero-decoration {
  position: absolute;
  top: -120px;
  right: -70px;

  width: 280px;
  height: 280px;

  pointer-events: none;

  background:
    radial-gradient(
      circle,
      rgba(69, 137, 243, 0.16),
      transparent 68%
    );
}

.hero-icon {
  width: 58px;
  height: 58px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  color: #ffffff;

  background:
    linear-gradient(
      145deg,
      #36a1ff,
      #4078f3 55%,
      #625bea
    );

  border-radius: 18px;

  box-shadow:
    0 10px 24px
    rgba(48, 126, 246, 0.25);
}

.hero-icon svg {
  width: 28px;
  height: 28px;
}

.hero-text {
  min-width: 0;
  flex: 1;
}

.hero-label,
.section-label,
.preview-label {
  color: #7f8a9b;

  font-family: "Eastman-Medium", sans-serif;
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.hero-text h1 {
  margin: 4px 0 5px;

  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  color: var(--text-primary);

  font-family: "Eastman-Bold", sans-serif;
  font-size: 22px;
  line-height: 1.25;
}

.hero-text p {
  max-width: 700px;

  margin: 0;

  color: var(--text-secondary);

  font-family: "Eastman-Regular", sans-serif;
  font-size: 12px;
  line-height: 1.55;
}

.changes-status {
  position: relative;
  z-index: 2;

  display: flex;
  align-items: center;
  gap: 8px;

  flex-shrink: 0;

  padding: 9px 12px;

  color: #64806e;
  background: rgba(47, 166, 102, 0.08);

  border: 1px solid rgba(47, 166, 102, 0.12);
  border-radius: 999px;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 10px;
}

.changes-status.active {
  color: #9a672a;
  background: rgba(229, 151, 56, 0.09);
  border-color: rgba(229, 151, 56, 0.14);
}

.status-dot {
  width: 7px;
  height: 7px;

  background: #36a267;
  border-radius: 50%;

  box-shadow:
    0 0 0 4px
    rgba(54, 162, 103, 0.1);
}

.changes-status.active .status-dot {
  background: #e29539;

  box-shadow:
    0 0 0 4px
    rgba(226, 149, 57, 0.12);
}

/* Layout */

.editor-layout {
  display: grid;
  grid-template-columns:
    minmax(0, 1fr)
    330px;

  align-items: start;
  gap: 24px;
}

.editor-main {
  min-width: 0;

  display: flex;
  flex-direction: column;
  gap: 20px;
}

.editor-card,
.preview-card,
.save-card {
  background: var(--card-background);

  border: 1px solid var(--card-border);
  border-radius: 24px;

  box-shadow:
    0 1px 2px
    rgba(15, 23, 42, 0.02),
    0 12px 34px
    rgba(15, 23, 42, 0.035);
}

.editor-card {
  padding: 26px;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 13px;
}

.section-header h2 {
  margin: 3px 0 0;

  color: var(--text-primary);

  font-family: "Eastman-Bold", sans-serif;
  font-size: 18px;
  line-height: 1.3;
}

.section-icon {
  width: 45px;
  height: 45px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  color: #3c82e1;
  background: rgba(51, 133, 240, 0.1);

  border: 1px solid rgba(51, 133, 240, 0.12);
  border-radius: 14px;
}

.section-icon.purple {
  color: #7366e8;
  background: rgba(104, 90, 229, 0.1);
  border-color: rgba(104, 90, 229, 0.12);
}

.section-icon.green {
  color: #299866;
  background: rgba(44, 159, 102, 0.09);
  border-color: rgba(44, 159, 102, 0.12);
}

.section-icon.orange {
  color: #dd8a34;
  background: rgba(222, 139, 52, 0.09);
  border-color: rgba(222, 139, 52, 0.12);
}

.section-icon.pink {
  color: #d95c8e;
  background: rgba(217, 92, 142, 0.09);
  border-color: rgba(217, 92, 142, 0.12);
}

.section-icon svg {
  width: 23px;
  height: 23px;
}

.section-description {
  max-width: 720px;

  margin: 17px 0 23px;

  color: var(--text-secondary);

  font-family: "Eastman-Regular", sans-serif;
  font-size: 12px;
  line-height: 1.6;
}

/* Fields */

.form-field + .form-field {
  margin-top: 23px;
}

.field-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  margin-bottom: 8px;
}

.field-heading label,
.experience-label {
  color: #404650;

  font-family: "Eastman-Medium", sans-serif;
  font-size: 11px;
}

.character-counter,
.optional-label {
  color: #9ba1ab;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 9px;
}

.character-counter.warning {
  color: #d28b32;
}

.input-shell {
  position: relative;

  width: 100%;
  min-height: 54px;

  display: flex;
  align-items: center;

  overflow: hidden;

  background: var(--input-background);
  border: 1px solid var(--input-border);
  border-radius: 15px;

  box-shadow:
    inset 0 1px 2px
    rgba(15, 23, 42, 0.025);

  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    transform 0.2s ease;
}

.input-shell:hover {
  background: var(--input-hover);
  border-color: #d3d8df;
}

.input-shell:focus-within {
  background: var(--card-background);
  border-color: #4e8ef4;

  box-shadow:
    0 0 0 4px
    rgba(78, 142, 244, 0.11),
    0 8px 22px
    rgba(38, 93, 181, 0.06);

  transform: translateY(-1px);
}

.input-shell.invalid,
.textarea-shell.invalid {
  border-color: #dc6268;

  box-shadow:
    0 0 0 4px
    rgba(220, 98, 104, 0.08);
}

.input-shell.valid:not(:focus-within),
.textarea-shell.valid:not(:focus-within) {
  border-color: rgba(41, 159, 98, 0.38);
}

.input-icon {
  width: 48px;
  height: 100%;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  color: #8d96a3;
}

.input-icon svg {
  width: 20px;
  height: 20px;
}

.input-shell:focus-within .input-icon {
  color: #4d88eb;
}

.text-input {
  min-width: 0;
  width: 100%;
  height: 52px;

  padding: 0 12px 0 0;

  color: var(--text-primary);
  caret-color: #4388f5;

  background: transparent;
  border: none;
  outline: none;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 14px;
}

.text-input::placeholder,
.salary-input::placeholder,
.description-textarea::placeholder {
  color: #a3a9b3;
}

.valid-status,
.textarea-valid-status {
  display: flex;
  align-items: center;
  justify-content: center;

  color: #299e65;
  background: rgba(41, 158, 101, 0.1);

  border-radius: 50%;
}

.valid-status {
  width: 27px;
  height: 27px;

  flex-shrink: 0;

  margin-right: 13px;
}

.textarea-valid-status {
  position: absolute;
  top: 13px;
  right: 13px;

  width: 29px;
  height: 29px;
}

.valid-status svg,
.textarea-valid-status svg {
  width: 16px;
  height: 16px;
}

.textarea-shell {
  position: relative;

  width: 100%;
  min-height: 300px;

  overflow: hidden;

  background: var(--input-background);
  border: 1px solid var(--input-border);
  border-radius: 16px;

  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    box-shadow 0.2s ease;
}

.textarea-shell:focus-within {
  background: var(--card-background);
  border-color: #4e8ef4;

  box-shadow:
    0 0 0 4px
    rgba(78, 142, 244, 0.11);
}

.description-textarea {
  width: 100%;
  min-height: 300px;

  display: block;

  padding:
    17px
    50px
    17px
    17px;

  resize: vertical;

  color: var(--text-primary);
  caret-color: #4388f5;

  background: transparent;
  border: none;
  outline: none;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 13px;
  line-height: 1.65;
}

.field-message {
  margin: 7px 3px 0;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 10px;
}

.error-message {
  color: #d35058;
}

/* Conditions */

.conditions-grid {
  display: grid;
  grid-template-columns:
    minmax(0, 0.9fr)
    minmax(0, 1.1fr);

  gap: 16px;
}

.salary-card,
.experience-card {
  padding: 17px;

  background: var(--input-background);

  border: 1px solid var(--input-border);
  border-radius: 17px;
}

.salary-row {
  display: flex;
  align-items: stretch;
  gap: 10px;
}

.salary-input-shell {
  min-width: 0;
  height: 48px;
  flex: 1;

  display: flex;
  align-items: center;

  background: var(--card-background);
  border: 1px solid var(--input-border);
  border-radius: 13px;
}

.salary-prefix {
  padding-left: 14px;

  color: #959da9;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 11px;
}

.salary-input {
  min-width: 0;
  width: 100%;
  height: 100%;

  padding: 0 12px;

  color: var(--text-primary);

  background: transparent;
  border: none;
  outline: none;

  font-family: "Eastman-Medium", sans-serif;
  font-size: 14px;
}

.currency-selector {
  display: flex;

  padding: 4px;

  background: var(--card-background);
  border: 1px solid var(--input-border);
  border-radius: 13px;
}

.currency-button {
  min-width: 36px;

  padding: 0 10px;

  cursor: pointer;

  color: #8c94a0;
  background: transparent;

  border: none;
  border-radius: 9px;

  font-family: "Eastman-Medium", sans-serif;
  font-size: 12px;

  transition:
    color 0.2s ease,
    background-color 0.2s ease,
    transform 0.2s ease;
}

.currency-button:hover {
  color: #4388f5;
}

.currency-button.active {
  color: #ffffff;
  background:
    linear-gradient(
      135deg,
      #318ef5,
      #526eea
    );

  box-shadow:
    0 5px 12px
    rgba(49, 126, 231, 0.23);
}

.experience-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;

  margin-bottom: 18px;
}

.experience-heading > div:first-child {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.experience-description {
  color: #949ba6;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 9px;
}

.experience-value {
  color: #3d7fdb;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 13px;
}

.experience-slider {
  width: 100%;
  height: 5px;

  appearance: none;
  cursor: pointer;

  background:
    linear-gradient(
      to right,
      #4388f5
      var(--slider-progress),
      #dfe3e9
      var(--slider-progress)
    );

  border-radius: 999px;
  outline: none;
}

.experience-slider::-webkit-slider-thumb {
  width: 19px;
  height: 19px;

  appearance: none;

  background: #ffffff;
  border: 5px solid #4388f5;
  border-radius: 50%;

  box-shadow:
    0 4px 10px
    rgba(42, 105, 204, 0.24);
}

.experience-slider::-moz-range-thumb {
  width: 10px;
  height: 10px;

  background: #ffffff;
  border: 5px solid #4388f5;
  border-radius: 50%;
}

.slider-labels {
  display: flex;
  justify-content: space-between;

  margin-top: 8px;

  color: #9aa1ab;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 8px;
}

.specific-section {
  margin-top: 20px;
}

.specific-wrapper {
  max-width: 540px;
}

/* Remote */

.remote-card {
  width: 100%;

  display: flex;
  align-items: center;
  gap: 13px;

  padding: 15px 16px;

  cursor: pointer;

  background: var(--input-background);

  border: 1px solid var(--input-border);
  border-radius: 16px;

  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    box-shadow 0.2s ease;
}

.remote-card.active {
  background:
    linear-gradient(
      135deg,
      rgba(48, 136, 255, 0.09),
      rgba(94, 91, 238, 0.06)
    );

  border-color: rgba(63, 135, 241, 0.28);

  box-shadow:
    0 8px 20px
    rgba(48, 117, 224, 0.06);
}

.remote-icon {
  width: 41px;
  height: 41px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  color: #6f7887;
  background: var(--card-background);

  border: 1px solid var(--card-border);
  border-radius: 12px;
}

.remote-card.active .remote-icon {
  color: #ffffff;
  background:
    linear-gradient(
      145deg,
      #319aff,
      #4a6ff0
    );

  border-color: transparent;
}

.remote-icon svg {
  width: 22px;
  height: 22px;
}

.remote-content {
  min-width: 0;

  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 3px;
}

.remote-content strong {
  color: var(--text-primary);

  font-family: "Eastman-Bold", sans-serif;
  font-size: 12px;
}

.remote-content span {
  color: var(--text-muted);

  font-family: "Eastman-Regular", sans-serif;
  font-size: 10px;
}

.toggle-input {
  position: absolute;

  width: 1px;
  height: 1px;

  opacity: 0;
}

.toggle-slider {
  position: relative;

  width: 47px;
  height: 27px;

  flex-shrink: 0;

  background: #d3d7de;
  border-radius: 999px;

  transition:
    background-color 0.25s ease;
}

.toggle-thumb {
  position: absolute;
  top: 4px;
  left: 4px;

  width: 19px;
  height: 19px;

  background: #ffffff;
  border-radius: 50%;

  box-shadow:
    0 2px 6px
    rgba(15, 23, 42, 0.2);

  transition:
    transform
    0.25s
    cubic-bezier(
      0.34,
      1.56,
      0.64,
      1
    );
}

.toggle-input:checked + .toggle-slider {
  background:
    linear-gradient(
      135deg,
      #2e97ff,
      #526cec
    );
}

.toggle-input:checked
+ .toggle-slider
.toggle-thumb {
  transform: translateX(20px);
}

.address-content {
  margin-top: 20px;
}

.address-shell {
  max-width: 660px;
}

.map-card {
  position: relative;

  width: 100%;
  max-width: 660px;
  height: 290px;

  overflow: hidden;

  margin-top: 15px;

  background: #e7ebf0;

  border: 1px solid var(--input-border);
  border-radius: 18px;

  box-shadow:
    0 8px 24px
    rgba(15, 23, 42, 0.07);
}

.map-iframe {
  width: 100%;
  height: 100%;

  border: none;
}

.map-placeholder {
  width: 100%;
  height: 100%;

  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;

  gap: 6px;

  color: #8b94a2;
  background: var(--input-background);

  font-family: "Eastman-Regular", sans-serif;
  font-size: 10px;
}

.map-placeholder strong {
  color: var(--text-primary);

  font-family: "Eastman-Bold", sans-serif;
  font-size: 12px;
}

.map-placeholder-icon {
  width: 42px;
  height: 42px;

  display: flex;
  align-items: center;
  justify-content: center;

  margin-bottom: 4px;

  color: #4388f5;
  background: rgba(67, 136, 245, 0.1);

  border-radius: 13px;
}

.map-placeholder-icon svg {
  width: 23px;
  height: 23px;
}

.map-bottom-gradient {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;

  height: 100px;

  pointer-events: none;

  background:
    linear-gradient(
      to top,
      rgba(20, 29, 44, 0.24),
      transparent
    );
}

.map-address {
  position: absolute;
  right: 12px;
  bottom: 12px;
  left: 12px;

  display: flex;
  align-items: center;
  gap: 10px;

  padding: 11px 12px;

  background:
    rgba(255, 255, 255, 0.92);

  backdrop-filter: blur(16px);

  border: 1px solid rgba(255, 255, 255, 0.7);
  border-radius: 13px;

  box-shadow:
    0 8px 24px
    rgba(15, 23, 42, 0.14);
}

.map-address-icon {
  width: 34px;
  height: 34px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  color: #ffffff;
  background:
    linear-gradient(
      145deg,
      #319aff,
      #4e6eef
    );

  border-radius: 10px;
}

.map-address-icon svg {
  width: 19px;
  height: 19px;
}

.map-address > div:last-child {
  min-width: 0;

  display: flex;
  flex-direction: column;
  gap: 2px;
}

.map-address span {
  color: #8b93a1;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 8px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.map-address strong {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  color: #303641;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 10px;
}

.remote-state {
  display: flex;
  align-items: center;
  gap: 14px;

  margin-top: 20px;
  padding: 20px;

  background:
    linear-gradient(
      135deg,
      rgba(48, 136, 255, 0.08),
      rgba(94, 91, 238, 0.05)
    );

  border: 1px dashed
    rgba(67, 136, 245, 0.24);

  border-radius: 17px;
}

.remote-state-icon {
  width: 54px;
  height: 54px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  color: #ffffff;
  background:
    linear-gradient(
      145deg,
      #32a0ff,
      #4e6def
    );

  border-radius: 17px;

  box-shadow:
    0 10px 22px
    rgba(48, 126, 246, 0.22);
}

.remote-state-icon svg {
  width: 28px;
  height: 28px;
}

.remote-state > div:last-child {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.remote-state strong {
  color: var(--text-primary);

  font-family: "Eastman-Bold", sans-serif;
  font-size: 12px;
}

.remote-state span {
  color: var(--text-secondary);

  font-family: "Eastman-Regular", sans-serif;
  font-size: 10px;
  line-height: 1.5;
}

/* Hashtags */

.hashtags-editor {
  width: 100%;

  display: flex;
  align-items: center;
  gap: 12px;

  min-height: 70px;

  padding: 14px 15px;

  cursor: pointer;
  outline: none;

  background: var(--input-background);
  border: 1px solid var(--input-border);
  border-radius: 16px;

  transition:
    border-color 0.2s ease,
    background-color 0.2s ease,
    box-shadow 0.2s ease;
}

.hashtags-editor:hover,
.hashtags-editor:focus-visible {
  background: var(--input-hover);
  border-color: rgba(67, 136, 245, 0.35);

  box-shadow:
    0 7px 18px
    rgba(35, 88, 172, 0.05);
}

.hashtags-list {
  min-width: 0;

  display: flex;
  flex: 1;
  flex-wrap: wrap;
  gap: 7px;
}

.hashtag-chip {
  padding: 7px 10px;

  color: #397bcf;
  background: rgba(57, 126, 215, 0.09);

  border: 1px solid rgba(57, 126, 215, 0.11);
  border-radius: 999px;

  font-family: "Eastman-Medium", sans-serif;
  font-size: 9px;
}

.hashtags-empty {
  flex: 1;

  color: var(--text-muted);

  font-family: "Eastman-Regular", sans-serif;
  font-size: 11px;
}

.hashtags-action {
  display: flex;
  align-items: center;
  gap: 5px;

  flex-shrink: 0;

  color: #4388f5;

  font-family: "Eastman-Medium", sans-serif;
  font-size: 10px;
}

.hashtags-action svg {
  width: 16px;
  height: 16px;
}

/* Upload */

.upload-area {
  position: relative;

  width: 100%;
  min-height: 330px;

  overflow: hidden;
  cursor: pointer;
  outline: none;

  background: var(--input-background);

  border: 1.5px dashed
    rgba(62, 137, 242, 0.45);

  border-radius: 20px;

  transition:
    background-color 0.22s ease,
    border-color 0.22s ease,
    box-shadow 0.22s ease,
    transform 0.22s ease;
}

.upload-area:hover,
.upload-area.dragging {
  background:
    rgba(49, 137, 255, 0.045);

  border-color: #4388f5;

  box-shadow:
    0 10px 27px
    rgba(45, 119, 225, 0.08);
}

.upload-placeholder,
.upload-loading {
  min-height: 330px;

  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;

  gap: 8px;

  text-align: center;
}

.upload-placeholder-icon {
  width: 58px;
  height: 58px;

  display: flex;
  align-items: center;
  justify-content: center;

  margin-bottom: 4px;

  color: #4388f5;
  background: rgba(67, 136, 245, 0.1);

  border: 1px solid
    rgba(67, 136, 245, 0.13);

  border-radius: 18px;
}

.upload-placeholder-icon svg {
  width: 28px;
  height: 28px;
}

.upload-placeholder strong,
.upload-loading strong {
  color: var(--text-primary);

  font-family: "Eastman-Bold", sans-serif;
  font-size: 13px;
}

.upload-placeholder span,
.upload-loading span {
  color: var(--text-secondary);

  font-family: "Eastman-Regular", sans-serif;
  font-size: 11px;
}

.upload-placeholder small {
  color: var(--text-muted);

  font-family: "Eastman-Regular", sans-serif;
  font-size: 9px;
}

.image-preview {
  position: relative;

  width: 100%;
  height: 330px;

  overflow: hidden;
}

.image-preview > img {
  width: 100%;
  height: 100%;

  display: block;

  object-fit: cover;

  transition:
    transform 0.4s ease,
    filter 0.3s ease;
}

.upload-area:hover .image-preview > img {
  transform: scale(1.025);
  filter: brightness(0.8);
}

.image-overlay {
  position: absolute;
  inset: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #ffffff;
  background:
    rgba(14, 22, 36, 0.34);

  opacity: 0;

  transition:
    opacity 0.22s ease;
}

.upload-area:hover .image-overlay {
  opacity: 1;
}

.image-overlay-content {
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 8px;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 11px;
}

.replace-image-icon {
  width: 42px;
  height: 42px;

  display: flex;
  align-items: center;
  justify-content: center;

  background: rgba(255, 255, 255, 0.18);
  backdrop-filter: blur(10px);

  border: 1px solid
    rgba(255, 255, 255, 0.25);

  border-radius: 13px;
}

.replace-image-icon svg {
  width: 21px;
  height: 21px;
}

.remove-image-button {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 4;

  display: flex;
  align-items: center;
  gap: 6px;

  padding: 8px 10px;

  cursor: pointer;

  color: #ffffff;
  background: rgba(28, 34, 46, 0.62);

  backdrop-filter: blur(10px);

  border: 1px solid
    rgba(255, 255, 255, 0.16);

  border-radius: 10px;

  font-family: "Eastman-Medium", sans-serif;
  font-size: 9px;
}

.remove-image-button:hover {
  background: #d95359;
}

.remove-image-button svg {
  width: 15px;
  height: 15px;
}

.upload-error {
  margin-top: 9px;

  color: #d45158;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 10px;
}

.hidden-input {
  display: none;
}

/* Sidebar */

.editor-sidebar {
  min-width: 0;
}

.sidebar-sticky {
  position: sticky;
  top: 24px;

  display: flex;
  flex-direction: column;
  gap: 16px;
}

.preview-card,
.save-card {
  padding: 18px;
}

.preview-label {
  display: block;
  margin-bottom: 12px;
}

.vacancy-preview {
  overflow: hidden;

  background: var(--input-background);

  border: 1px solid var(--input-border);
  border-radius: 17px;
}

.preview-image-wrapper {
  position: relative;

  width: 100%;
  height: 155px;

  overflow: hidden;

  background:
    linear-gradient(
      135deg,
      rgba(48, 136, 255, 0.12),
      rgba(94, 91, 238, 0.08)
    );
}

.preview-image-wrapper > img {
  width: 100%;
  height: 100%;

  display: block;
  object-fit: cover;
}

.preview-placeholder {
  width: 100%;
  height: 100%;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #8a96a8;
}

.preview-placeholder svg {
  width: 34px;
  height: 34px;
}

.preview-work-type {
  position: absolute;
  top: 10px;
  right: 10px;

  padding: 6px 9px;

  color: #ffffff;
  background:
    linear-gradient(
      135deg,
      #318ef5,
      #566be8
    );

  border-radius: 999px;

  font-family: "Eastman-Medium", sans-serif;
  font-size: 8px;
}

.preview-content {
  padding: 15px;
}

.preview-specific {
  color: #4388f5;

  font-family: "Eastman-Medium", sans-serif;
  font-size: 8px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.preview-content h3 {
  margin: 6px 0 8px;

  color: var(--text-primary);

  font-family: "Eastman-Bold", sans-serif;
  font-size: 14px;
  line-height: 1.35;
}

.preview-content p {
  display: -webkit-box;
  overflow: hidden;

  margin: 0;

  color: var(--text-secondary);

  font-family: "Eastman-Regular", sans-serif;
  font-size: 9px;
  line-height: 1.5;

  -webkit-box-orient: vertical;
  -webkit-line-clamp: 4;
}

.preview-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;

  margin-top: 13px;
}

.preview-meta span {
  padding: 6px 8px;

  color: #687488;
  background: var(--card-background);

  border: 1px solid var(--input-border);
  border-radius: 8px;

  font-family: "Eastman-Medium", sans-serif;
  font-size: 8px;
}

.save-card-heading {
  display: flex;
  flex-direction: column;
  gap: 5px;

  margin-bottom: 16px;
}

.save-card-heading strong {
  color: var(--text-primary);

  font-family: "Eastman-Bold", sans-serif;
  font-size: 14px;
}

.save-card-heading span {
  color: var(--text-secondary);

  font-family: "Eastman-Regular", sans-serif;
  font-size: 9px;
  line-height: 1.5;
}

.validation-list {
  display: flex;
  flex-direction: column;
  gap: 9px;

  margin-bottom: 17px;
}

.validation-item {
  display: flex;
  align-items: center;
  gap: 8px;

  color: #9299a5;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 9px;
}

.validation-item.valid {
  color: #4a8060;
}

.validation-icon {
  width: 20px;
  height: 20px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  color: #a0a6b0;
  background: var(--input-background);

  border-radius: 50%;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 9px;
}

.validation-item.valid .validation-icon {
  color: #ffffff;
  background: #30a269;
}

.save-button,
.cancel-button {
  width: 100%;
  min-height: 50px;

  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  cursor: pointer;

  border: none;
  border-radius: 15px;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 11px;

  transition:
    opacity 0.2s ease,
    transform 0.2s ease,
    box-shadow 0.2s ease;
}

.save-button {
  color: #ffffff;
  background:
    linear-gradient(
      110deg,
      #2887f7,
      #526de8,
      #34a9f4,
      #2887f7
    );

  background-size: 220% 100%;

  box-shadow:
    0 9px 20px
    rgba(40, 135, 247, 0.24);

  animation:
    button-gradient
    3s
    linear
    infinite;
}

.save-button:hover:not(:disabled) {
  transform: translateY(-1px);

  box-shadow:
    0 12px 25px
    rgba(40, 135, 247, 0.32);
}

.save-button:disabled {
  cursor: not-allowed;
  opacity: 0.38;
  box-shadow: none;
}

.save-button svg {
  width: 17px;
  height: 17px;
}

.cancel-button {
  margin-top: 9px;

  color: #686f7a;
  background: var(--input-background);
}

.cancel-button:hover:not(:disabled) {
  background: var(--input-hover);
}

.button-loader {
  width: 17px;
  height: 17px;

  border: 2px solid
    rgba(255, 255, 255, 0.4);

  border-top-color: #ffffff;
  border-radius: 50%;

  animation:
    spin
    0.75s
    linear
    infinite;
}

.edit-footer {
  flex-shrink: 0;
}

/* Transitions */

.status-enter-active,
.status-leave-active,
.message-enter-active,
.message-leave-active,
.address-enter-active,
.address-leave-active,
.image-enter-active,
.image-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.status-enter-from,
.status-leave-to {
  opacity: 0;
  transform: scale(0.7);
}

.message-enter-from,
.message-leave-to {
  opacity: 0;
  transform: translateY(-3px);
}

.address-enter-from {
  opacity: 0;
  transform: translateY(8px);
}

.address-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}

.image-enter-from,
.image-leave-to {
  opacity: 0;
  transform: scale(0.985);
}

/* Dark theme */

html[data-theme="dark"] .edit-page {
  --page-background: #111113;
  --card-background: #1c1c1e;
  --card-border: rgba(255, 255, 255, 0.07);
  --text-primary: #f0f1f3;
  --text-secondary: #a5a9b2;
  --text-muted: #7f858f;
  --input-background: #28282c;
  --input-border: #3b3b41;
  --input-hover: #2c2c31;

  background:
    radial-gradient(
      circle at 15% 0%,
      rgba(40, 135, 247, 0.08),
      transparent 420px
    ),
    radial-gradient(
      circle at 95% 15%,
      rgba(110, 84, 232, 0.06),
      transparent 440px
    ),
    var(--page-background);
}

html[data-theme="dark"] .hero-card {
  background:
    linear-gradient(
      135deg,
      rgba(40, 135, 247, 0.1),
      rgba(99, 83, 232, 0.06)
    ),
    #1c1c1e;

  border-color:
    rgba(76, 141, 240, 0.14);
}

html[data-theme="dark"]
.field-heading label,
html[data-theme="dark"]
.experience-label {
  color: #d8dae0;
}

html[data-theme="dark"]
.input-shell:hover {
  border-color: #484850;
}

html[data-theme="dark"]
.input-shell:focus-within,
html[data-theme="dark"]
.textarea-shell:focus-within {
  background: #2d2d32;
  border-color: #548ff1;

  box-shadow:
    0 0 0 4px
    rgba(78, 143, 245, 0.13);
}

html[data-theme="dark"]
.text-input,
html[data-theme="dark"]
.salary-input,
html[data-theme="dark"]
.description-textarea {
  color: #f2f2f4;
}

html[data-theme="dark"]
.text-input::placeholder,
html[data-theme="dark"]
.salary-input::placeholder,
html[data-theme="dark"]
.description-textarea::placeholder {
  color: #6f727b;
}

html[data-theme="dark"]
.salary-card,
html[data-theme="dark"]
.experience-card {
  background: #252529;
}

html[data-theme="dark"]
.salary-input-shell,
html[data-theme="dark"]
.currency-selector {
  background: #303035;
  border-color: #414148;
}

html[data-theme="dark"]
.experience-slider {
  background:
    linear-gradient(
      to right,
      #548ff1
      var(--slider-progress),
      #44454b
      var(--slider-progress)
    );
}

html[data-theme="dark"]
.remote-card {
  background: #26262a;
  border-color: #38383e;
}

html[data-theme="dark"]
.remote-card.active {
  background:
    linear-gradient(
      135deg,
      rgba(48, 132, 255, 0.13),
      rgba(94, 91, 238, 0.09)
    );

  border-color:
    rgba(69, 140, 248, 0.3);
}

html[data-theme="dark"]
.remote-icon {
  background: #303035;
  border-color: #414147;
}

html[data-theme="dark"]
.toggle-slider {
  background: #48484f;
}

html[data-theme="dark"]
.map-card {
  background: #27282c;
  border-color: #3a3b41;

  box-shadow:
    0 10px 28px
    rgba(0, 0, 0, 0.2);
}

html[data-theme="dark"]
.map-address {
  background:
    rgba(37, 38, 43, 0.92);

  border-color:
    rgba(255, 255, 255, 0.08);

  box-shadow:
    0 9px 28px
    rgba(0, 0, 0, 0.28);
}

html[data-theme="dark"]
.map-address strong {
  color: #f0f1f3;
}

html[data-theme="dark"]
.hashtag-chip {
  color: #77a9ed;
  background:
    rgba(67, 136, 245, 0.1);

  border-color:
    rgba(67, 136, 245, 0.14);
}

html[data-theme="dark"]
.upload-area {
  background: #25262a;
  border-color:
    rgba(75, 144, 246, 0.36);
}

html[data-theme="dark"]
.upload-area:hover,
html[data-theme="dark"]
.upload-area.dragging {
  background: #292a2f;
  border-color: #548ff1;
}

html[data-theme="dark"]
.preview-meta span {
  color: #a2a9b5;
}

html[data-theme="dark"]
.cancel-button {
  color: #b1b5bd;
}

/* Responsive */

@media (max-width: 1100px) {
  .editor-layout {
    grid-template-columns: 1fr;
  }

  .editor-sidebar {
    order: -1;
  }

  .sidebar-sticky {
    position: static;

    display: grid;
    grid-template-columns:
      minmax(0, 1fr)
      minmax(0, 1fr);
  }
}

@media (max-width: 760px) {
  .edit-content {
    padding: 20px 16px;
  }

  .hero-card {
    align-items: flex-start;

    padding: 20px;
  }

  .changes-status {
    display: none;
  }

  .editor-card {
    padding: 21px 18px;
  }

  .conditions-grid {
    grid-template-columns: 1fr;
  }

  .sidebar-sticky {
    grid-template-columns: 1fr;
  }

  .salary-row {
    flex-direction: column;
  }

  .currency-selector {
    width: fit-content;
  }
}

@media (max-width: 520px) {
  .hero-card {
    flex-wrap: wrap;
  }

  .hero-icon {
    width: 49px;
    height: 49px;

    border-radius: 15px;
  }

  .hero-text h1 {
    font-size: 18px;
  }

  .hero-text p {
    font-size: 11px;
  }

  .section-header h2 {
    font-size: 16px;
  }

  .section-icon {
    width: 41px;
    height: 41px;

    border-radius: 13px;
  }

  .description-textarea {
    min-height: 240px;
  }

  .textarea-shell {
    min-height: 240px;
  }

  .remote-icon {
    display: none;
  }

  .remote-card {
    gap: 10px;
  }

  .remote-content span {
    padding-right: 5px;
  }

  .map-card {
    height: 230px;
  }

  .hashtags-editor {
    align-items: flex-start;
  }

  .hashtags-action {
    margin-top: 4px;
  }

  .upload-area,
  .upload-placeholder,
  .upload-loading,
  .image-preview {
    min-height: 250px;
    height: 250px;
  }
}

/* Animations */

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes button-gradient {
  from {
    background-position: 0 50%;
  }

  to {
    background-position: 220% 50%;
  }
}

@media (prefers-reduced-motion: reduce) {
  .save-button,
  .button-loader,
  .loader-ring {
    animation: none;
  }

  .input-shell,
  .textarea-shell,
  .remote-card,
  .toggle-slider,
  .toggle-thumb,
  .upload-area,
  .image-preview img,
  .image-overlay,
  .status-enter-active,
  .status-leave-active,
  .message-enter-active,
  .message-leave-active,
  .address-enter-active,
  .address-leave-active,
  .image-enter-active,
  .image-leave-active {
    transition: none;
  }
}
</style>